import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useContext, useState } from "react"
import { useMutation } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import Form from "src/components/Form"
import QRScanner from "src/components/QRScanner"

import { scanDockwaPayToken } from "src/api/DockwaPay"

import { useToast } from "src/hooks/use_toast"
import useWindowSize from "src/hooks/use_window_size"

import { POSContext } from "../Layout"
import SmallScreenTopNav from "./SmallScreenTopNav"

const SmallScreenDockwaPay = ({ handleSuccess }) => {
  const showToast = useToast()
  const { height } = useWindowSize()
  const { setDockwaPayModalOpen, iosWebview } = useContext(POSContext)
  const [scanTabActive, setScanTabActive] = useState(true)

  const { marinaSlug } = useMarina()

  const [manualEntryCode, setManualEntryCode] = useState("")

  const { mutate: scanToken, isLoading } = useMutation({
    mutationFn: (code) => scanDockwaPayToken({ marinaSlug, code }),
    onSuccess: (data) => {
      handleSuccess(data)
    },
    onError: () => {
      showToast(
        "An error has occurred. Please try again or contact support at mayday@dockwa.com.",
        { type: "error" }
      )
    },
  })

  const resetManualEntry = () => {
    setManualEntryCode("")
  }

  const handleScan = (scanResult) => {
    resetManualEntry()
    scanToken(scanResult)
  }

  const handleManualCodeEntry = () => {
    scanToken(manualEntryCode)
  }

  const toggleScanTab = (scanTabActive) => {
    resetManualEntry()
    setScanTabActive(scanTabActive)
  }

  const renderTabs = () => {
    return (
      <div className="absolute top-0 z-10 w-full p-4">
        <div className="flex w-full rounded bg-gray-200 p-1">
          <div className="flex flex-1">
            <Button
              fullWidth
              title="Scan QR Code"
              variant={scanTabActive ? "primary" : "ghost-tertiary"}
              onClick={() => toggleScanTab(true)}
            >
              Scan QR Code
            </Button>
          </div>
          <div className="flex flex-1">
            <Button
              fullWidth
              title="Enter Manual Code"
              variant={scanTabActive ? "ghost-tertiary" : "primary"}
              onClick={() => toggleScanTab(false)}
            >
              Enter Manual Code
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const renderManualCodeEntry = () => {
    return (
      <div className="flex h-full flex-col justify-between p-4 pt-24">
        <div className="flex flex-col items-center space-y-4 text-center">
          <Form.TextField
            id="dockwa-pay-manual-code-entry-mobile"
            placeholder="Enter code"
            value={manualEntryCode}
            onChange={(e) => setManualEntryCode(e.target.value)}
          />
          <div className="text-gray-600">
            This code can be found on the Dockwa Pay QR code page of the
            customer’s Dockwa app
          </div>
        </div>

        <Button
          fullWidth
          variant="primary"
          title="Continue"
          onClick={handleManualCodeEntry}
        >
          Continue
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-grow flex-col">
      <SmallScreenTopNav
        headerText="Dockwa Pay"
        onClick={() => setDockwaPayModalOpen(false)}
      />
      <div
        className={classNames("relative overflow-x-hidden bg-white", {
          "h-full overflow-y-scroll": iosWebview,
          "h-[calc(100dvh-160px)]": !iosWebview,
          "overflow-y-scroll": !iosWebview && height <= 550,
          "overflow-y-hidden": !iosWebview && height > 550,
        })}
      >
        {renderTabs()}
        {scanTabActive ? (
          <QRScanner
            onScan={handleScan}
            onError={() =>
              showToast(
                "An error has occurred. Please try again or contact support at mayday@dockwa.com.",
                { type: "error" }
              )
            }
            fullScreenMobile
            isLoadingScanResult={isLoading}
            scanResultLoadingText="Fetching customer info..."
          />
        ) : (
          renderManualCodeEntry()
        )}
      </div>
    </div>
  )
}

SmallScreenDockwaPay.propTypes = {
  handleSuccess: PropTypes.func.isRequired,
}
export default SmallScreenDockwaPay
